import { HTMLTable, Label } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramClassDrawerRelationshipsAddRow } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationshipsAddRow';
import { ProjectDiagramClassDrawerRelationshipsEditRow } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationshipsEditRow';
import { ProjectDiagramClassDrawerRelationshipsRow } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationshipsRow';
import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import * as copy from 'src/templates/copy';

type ProjectDiagramClassDrawerRelationshipsProps = {
  additionalIris?: Set<string>;
  canUpdateDirectly?: boolean;
  relationships: ClassRelationshipItem[];
  onAddRelationship: (item: ClassRelationshipItem) => void;
  onClickRelationship?: (item: ClassRelationshipItem) => void;
  onUnlinkRelationship: (item: ClassRelationshipItem) => void;
  onUpdateRelationship: (item: ClassRelationshipItem) => void;
};

export const ProjectDiagramClassDrawerRelationships: React.VFC<
  ProjectDiagramClassDrawerRelationshipsProps
> = ({
  additionalIris,
  canUpdateDirectly,
  relationships,
  onAddRelationship,
  onClickRelationship,
  onUnlinkRelationship,
  onUpdateRelationship,
}) => {
  const [editId, setEditId] = React.useState<string | null>(null);
  const { highlightedIndices, sortedRelationships } = React.useMemo(() => {
    const sortedRelationships = relationships.sort(
      alphaSort(({ text }) => text)
    );
    const highlightedIndices = sortedRelationships.reduce<Set<number>>(
      (acc, relationship, idx) => {
        if (relationship.highlighted) {
          acc.add(idx);
        }
        return acc;
      },
      new Set()
    );
    return { highlightedIndices, sortedRelationships };
  }, [relationships]);

  return (
    <div>
      <Label className="sd-drawer-project-diagram-entity-table-label">
        {copy.components.projectDiagram.classDrawer.relationshipsTable.label}
      </Label>
      <HTMLTable
        className={classNames(
          'sd-drawer-project-diagram-entity-table',
          'sd-drawer-project-diagram-entity-table--class-relationships'
        )}
        data-testid={
          TEST_IDS.projectDiagram.entityDrawer.classRelationshipsTable
        }
      >
        <thead>
          <tr>
            <th>
              {
                copy.components.projectDiagram.classDrawer.relationshipsTable
                  .nameColumn
              }
            </th>
            <th>
              {
                copy.components.projectDiagram.classDrawer.relationshipsTable
                  .directionColumn
              }
            </th>
            <th>{}</th>
          </tr>
        </thead>

        <tbody>
          <ProjectDiagramClassDrawerRelationshipsAddRow
            additionalIris={additionalIris}
            currentRelationships={relationships}
            onSubmit={onAddRelationship}
          />
          {sortedRelationships.map((relationship, idx) =>
            canUpdateDirectly || editId === relationship.value ? (
              <ProjectDiagramClassDrawerRelationshipsEditRow
                key={relationship.value}
                canUpdateDirectly={canUpdateDirectly}
                idx={idx}
                onSubmit={(rel) => {
                  if (rel) {
                    onUpdateRelationship(rel);
                  }
                  setEditId(null);
                }}
                onUnlink={onUnlinkRelationship}
                relationship={relationship}
              />
            ) : (
              <ProjectDiagramClassDrawerRelationshipsRow
                key={relationship.value}
                idx={idx}
                isPreviousRowHighlighted={highlightedIndices.has(idx - 1)}
                onClickEdit={(rel) => setEditId(rel.value)}
                onClickLink={onClickRelationship}
                onUnlink={onUnlinkRelationship}
                relationship={relationship}
              />
            )
          )}
        </tbody>
      </HTMLTable>
    </div>
  );
};
